// MaintenancePage.tsx
import React, {FC, useEffect, useState, useCallback} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {ToastContainer} from 'react-toastify' 
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import TextAreaInput from '../../../../shared/components/TextAreaInput'
import {
  IMaintenanceModel,
  validationMaintenance,
  initialMaintenance,
  CURRENCY_OPTIONS,
  STATUS_OPTIONS,
  PAYER_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  CASH_REGISTER_CODE_OPTIONS,
  IBAN_OPTIONS,
  READYDESRIPTION_OPTIONS,
} from './ServiceMaintenanceModel'
import CheckboxInput from '../../../../shared/components/CheckboxInput'
import {
  branches,
  fetchAsyncCarData,
  fetchAsyncCustomerData,
} from '../../../../shared/models/SharedApiLookups'
import {loadMaintenances, createMaintenance, deleteMaintenance, updateMaintenance} from './Api'
import {MaintenanceTable} from './ServiceMaintenanceTable'  
import AsyncSearchInput from '../../../../shared/components/AsyncSearchInput'
import {AsyncCarModel, AsyncCustomerModel} from '../../../../shared/models/ApiModels'

// --- Sayfa Bileşeni ---

interface Option {
  value: string
  label: string
}

const MaintenancePage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Liste için bakım kayıtları ve sayfalama bilgileri
  const [maintenancesList, setMaintenancesList] = useState<IMaintenanceModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [tab, setTab] = useState<string | 'form'>('form')
  const [car, setCar] = useState<Option | null>(null) 

  // Şube seçenekleri için state
  const [branchesOptions, setBranchesOptions] = useState<{value: string; label: string}[]>([])

  const tabOptions: TabOption<
    'form' | 'vehicle' | 'payment' | 'pricing' | 'filePicture' | 'fatur' | 'list'
  >[] = [
    {key: 'form', title: 'Bakım Bilgileri', icon: 'bi bi-tools'},
    {key: 'payment', title: 'Ödeme', icon: 'bi bi-currency-dollar'},
    {key: 'vehicle', title: 'Araç', icon: 'bi bi-car-front'},
    {key: 'pricing', title: 'Fiyatlandırma', icon: 'bi bi-cash-stack'},
    {key: 'filePicture', title: 'Dosya Resim', icon: 'bi bi-file-image'},
    {key: 'fatur', title: 'Muhasebe-Fatura', icon: 'bi bi-receipt'},
    {key: 'list', title: 'Bakım Listesi', icon: 'bi bi-list-ul'},
  ]

  // --- Veri Çekme Fonksiyonları ---

  // Şube verilerini API'den çekme
  const fetchBranches = useCallback(async () => {
    setLoading(true)
    try {
      const fetchedBranches = await branches()
      const options = fetchedBranches.map((b: any) => ({
        value: b.id.toString(), // ID'nin string olduğundan emin ol
        label: b.name,
      }))
      setBranchesOptions(options)
    } catch (err) {
      console.error('Error fetching branches:', err)
      //showErrorToast("Şubeler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false)
    }
  }, [])

  // Bakım listesini API'den çekme (Paginasyon ve sıralama destekli)
  const fetchMaintenanceList = useCallback(
    async (page: number, size: number, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true)
      setError(null)
      try {
        const response = await loadMaintenances(page, size, sort, order)

        if (response.data.hasError) {
          throw new Error(response.data.message || 'Bakım listesi yüklenemedi.')
        }

        setMaintenancesList(response.data.payload.content || [])
        setTotalPages(response.data.payload.totalPages || 0)
        setCurrentPage(page) // Sayfa numarasını güncelle
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Bilinmeyen bir hata oluştu.'
        setError(message)
        showErrorToast(`Liste yüklenirken hata: ${message}`)
      } finally {
        setLoading(false)
      }
    },
    [] // Bağımlılık dizisi boş, çünkü loadMaintenances ve showErrorToast değişmiyor.
  )
  // --- Etki Hook'ları (Effects) ---

  // Component yüklendiğinde şube verilerini yükle
  useEffect(() => {
    fetchBranches()
  }, [fetchBranches])

  // Tab değiştiğinde veya sayfalama/sıralama değiştiğinde bakım listesini yükle
  useEffect(() => {
    if (tab === 'list') {
      fetchMaintenanceList(currentPage, 10, sortBy, sortOrder)
    }
  }, [tab, currentPage, sortBy, sortOrder, fetchMaintenanceList])

  // --- İşlem Fonksiyonları ---

  const handleSave = async (values: IMaintenanceModel, resetForm: () => void) => {
    setLoading(true)
    setError(null) // Hata durumunu temizle
    try {
      if (selectedId) {
        // Güncelleme işlemi
        await updateMaintenance({...values, id: selectedId})
        showSuccessToast('Bakım Servisi başarıyla güncellendi.')
      } else {
        // Oluşturma işlemi
        await createMaintenance(values)
        showSuccessToast('Bakım Servisi başarıyla oluşturuldu.')
      }
      // İşlem sonrası state'leri güncelle
      resetForm() // Formu temizle
      setSelectedId(null) // Seçili ID'yi kaldır
      setTab('list') // Listeye dön
      fetchMaintenanceList(0, 10, sortBy, sortOrder) // Listeyi ilk sayfadan yeniden yükle
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Bilinmeyen bir hata oluştu.'
      setError(message)
      showErrorToast(`Kaydetme işlemi sırasında hata: ${message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (selectedId) {
      setLoading(true)
      try {
        await deleteMaintenance(selectedId)
        showSuccessToast('Bakım kaydı başarıyla silindi.')
        // Silme sonrası listeyi yeniden yükle
        fetchMaintenanceList(currentPage, 10, sortBy, sortOrder)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Bilinmeyen bir hata oluştu.'
        setError(message)
        showErrorToast(`Silme işlemi sırasında hata: ${message}`)
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  // Formu temizle ve yeni kayıt moduna geç
  const handleResetAndClear = (formik: FormikProps<IMaintenanceModel>) => {
    formik.resetForm() // Formik state'ini sıfırla
    setSelectedId(null) // Seçili ID'yi kaldır
    setTab('form') // Form sekmesine geç
  }

  // Düzenleme için veriyi forma yükle
  const handleEdit = useCallback(
    (maintenance: IMaintenanceModel, formik: FormikProps<IMaintenanceModel>) => {
      formik.setValues(maintenance) // Formik değerlerini ayarla
      setSelectedId(maintenance.id) // ID'yi kaydet
      setTab('form') // Form sekmesine geç
    },
    []
  )

  const handleDeleteClick = useCallback((maintenance: IMaintenanceModel) => {
    setSelectedId(maintenance.id)
    setShowModal(true)
  }, [])

  // Sayfa numarası değiştiğinde listeyi yeniden yükle
  const handlePageChange = useCallback(
    (page: number) => {
      if (page >= 0 && page < totalPages) {
        setCurrentPage(page)
        fetchMaintenanceList(page, 10, sortBy, sortOrder) // Yeni sayfayı çek
      }
    },
    [totalPages, sortBy, sortOrder, fetchMaintenanceList]
  )

  // Sıralama değiştiğinde listeyi yeniden yükle
  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      setCurrentPage(0) // Sıralama değiştiğinde ilk sayfaya dön
      fetchMaintenanceList(0, 10, column, order) // Sıralı ve filtrelenmiş veriyi çek
    },
    [fetchMaintenanceList]
  )

  return (
    <>
      <Formik
        initialValues={initialMaintenance}
        validationSchema={validationMaintenance}
        enableReinitialize // selectedId değiştiğinde formun yeniden initialize olmasını sağlar
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          // ✅ Formik render fonksiyonu içinde setFieldValue
          const {setFieldValue} = formik

          // Araç seçimi değiştiğinde Formik değerlerini güncelle
          const handleCarChange = async (car: Option | null) => {
            setCar(car)
            if (car?.value) {
              const response = await fetch(`/api/v1/car/${car.value}`)
              const data = await response.json()

              setFieldValue('plate', data.plate)
              setFieldValue('brand', data.brand)
              setFieldValue('type', data.type)
              setFieldValue('year', data.year)
              setFieldValue('gear', data.gear)
              setFieldValue('fuelType', data.fuelType)
              setFieldValue('vehicleGroup', data.vehicleGroup)
            }
          }

          return (
            <>
              <FormTabHeader
                formId='maintenanceForm'
                formikSubmit={formik.handleSubmit}
                formikReset={() => handleResetAndClear(formik)}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />
              <div className='card mb-5'>
                {/* Form ve Detail Sekmeleri */}
                {(tab === 'form' ||
                  tab === 'payment' ||
                  tab === 'vehicle' ||
                  tab === 'pricing' ||
                  tab === 'fatur') && (
                  <Form id='maintenanceForm' className='form card-body'>
                    {tab === 'form' && (
                      <>
                        <div className='row mb-6'>
                          <TextInput
                            name='date'
                            label='Tarih'
                            type='date'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput
                            name='expenseName'
                            label='Gider Adı'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput
                            name='amount'
                            label='Tutar'
                            type='number'
                            wrapperClassName='col-md-3'
                          />
                          <SelectInput
                            name='currency'
                            label='Para Birimi'
                            wrapperClassName='col-md-3'
                            options={CURRENCY_OPTIONS}
                          />
                          <SelectInput
                            name='branchId'
                            label='İşlem Şube'
                            wrapperClassName='col-md-6'
                            options={branchesOptions}
                          />
                        </div>

                        <div className='row mb-6'>
                          <TextInput
                            name='tax'
                            label='Vergi'
                            type='number'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput
                            name='documentNo'
                            label='Evrak No'
                            type='number'
                            wrapperClassName='col-md-6'
                          />
                        </div>

                        <div className='row mb-6'>
                          <TextInput
                            name='remainder'
                            label='Kalan'
                            type='number'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput
                            name='transactor'
                            label='İşlem Yapan'
                            wrapperClassName='col-md-6'
                          />
                        </div>

                        <div className='row mb-6'>
                          <AsyncSearchInput<AsyncCarModel>
                            name="carId"
                            label='Araç Ara'
                            placeholder='Araç bilgisi girin...'
                            fetchHandler={fetchAsyncCarData}
                            displayField={(item) => `${item.plate} (${item.modelYear})`}
                            onSelect={(item) => console.log('Seçilen araç:', item)}
                            onClear={() => console.log('Araç temizlendi')}
                          />
                          <AsyncSearchInput<AsyncCustomerModel>
                            name="customerId"
                            label='Cari Ara'
                            placeholder='Ad Soyad girin...'
                            fetchHandler={fetchAsyncCustomerData}
                            displayField={(item) => `${item.firstName} ${item.lastName}`}
                            onSelect={(item) => console.log('Seçilen cari:', item)}
                            onClear={() => console.log('Cari temizlendi')}
                          />
                        </div>

                        <div className='row mb-6'>
                          <TextInput
                            name='dateSent'
                            label='Gönderilme Tarihi'
                            type='date'
                            wrapperClassName='col-md-4'
                          />
                          <TextInput
                            name='timeSent'
                            label='Gönderilme Saati'
                            type='time'
                            wrapperClassName='col-md-2'
                          />
                          <SelectInput
                            name='status'
                            label='Durum'
                            options={STATUS_OPTIONS}
                            wrapperClassName='col-md-6'
                          />
                        </div>

                        <div className='row mb-6'>
                          <TextInput
                            name='plannedDate'
                            label='Planlanan Tarih'
                            type='date'
                            wrapperClassName='col-md-4'
                          />
                          <TextInput
                            name='plannedTime'
                            label='Planlanan Saat'
                            type='time'
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='accidentResponsible'
                            label='Kaza Sorumlusu'
                            wrapperClassName='col-md-6'
                          />
                        </div>

                        <div className='row mb-6'>
                          <TextAreaInput
                            name='previousDescription'
                            label='Önce Açıklama'
                            wrapperClassName='col-md-6'
                          />
                          <TextAreaInput
                            name='nextDescription'
                            label='Sonra Açıklama'
                            wrapperClassName='col-md-6'
                          />
                        </div>

                        <div className='row mb-6'>
                          <TextInput
                            name='outKM'
                            label='Çıkış KM'
                            type='number'
                            wrapperClassName='col-md-3'
                          />
                          <TextInput
                            name='outFuelLevel'
                            label='Çıkış Yakıt Seviyesi'
                            wrapperClassName='col-md-3'
                          />
                          <TextInput
                            name='inKM'
                            label='Dönüş KM'
                            type='number'
                            wrapperClassName='col-md-3'
                          />
                          <TextInput
                            name='inFuelLevel'
                            label='Dönüş Yakıt Seviyesi'
                            wrapperClassName='col-md-3'
                          />
                        </div>

                        <div className='row mb-6'>
                          <label
                            style={{
                              fontSize: '16px',
                              fontWeight: 'bold',
                              marginBottom: '6px',
                              display: 'block',
                            }}
                          >
                            {' '}
                            İşlem Türü{' '}
                          </label>

                          <CheckboxInput
                            name='periodicService'
                            label='Periyodik Bakım'
                            wrapperClassName='col-md-3'
                          />
                          <CheckboxInput
                            name='damageAccident'
                            label='Hasar / Kaza'
                            wrapperClassName='col-md-3'
                          />
                          <TextInput
                            name='endDate'
                            label='Bitiş Tarihi'
                            type='date'
                            wrapperClassName='col-md-3'
                          />
                          <TextInput
                            name='endTime'
                            label='Bitiş Saati'
                            type='time'
                            wrapperClassName='col-md-3'
                          />
                        </div>

                        <div className='row mb-6'>
                          <CheckboxInput
                            name='mechanicalFailure'
                            label='Mekanik Arıza'
                            wrapperClassName='col-md-3'
                          />
                          <CheckboxInput
                            name='maintenance'
                            label='Bakım'
                            wrapperClassName='col-md-3'
                          />
                          <TextInput
                            name='warningKm'
                            label='Uyarı KM'
                            wrapperClassName='col-md-6'
                          />
                        </div>

                        <div className='row mb-6'>
                          <SelectInput
                            name='payer'
                            label='Kim Ödeyecek'
                            wrapperClassName='col-md-6'
                            options={PAYER_OPTIONS}
                          />
                        </div>
                      </>
                    )}
                    {tab === 'payment' && (
                      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 p-5'>
                        <div className='flex flex-col gap-4'>
                          <div className='row mb-6'>
                            <TextInput
                              name='paymentDate'
                              label='Ödeme Tarihi'
                              type='date'
                              wrapperClassName='col-md-6'
                            />
                            <TextAreaInput
                              name='description'
                              label='Açıklama'
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='payment'
                              label='Ödeme'
                              type='number'
                              wrapperClassName='col-md-3'
                            />
                            <SelectInput
                              name='currency'
                              label='Para Birimi'
                              wrapperClassName='col-md-3'
                              options={CURRENCY_OPTIONS}
                            />
                            <SelectInput
                              name='readyDescription'
                              label='Hazır Açıklama'
                              wrapperClassName='col-md-6'
                              options={READYDESRIPTION_OPTIONS}
                            />
                          </div>
                          <div className='row mb-6'>
                            <SelectInput
                              name='paymentType'
                              label='Ödeme Türü'
                              options={PAYMENT_TYPE_OPTIONS}
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='agreementNo'
                              label='Sözleşme No'
                              wrapperClassName='col-md-6'
                            />
                            <SelectInput
                              name='cashRegisterCode'
                              label='Kasa Kodu'
                              options={CASH_REGISTER_CODE_OPTIONS}
                              wrapperClassName='col-md-6'
                            />
                            <SelectInput
                              name='iban'
                              label='IBAN (Hesap No)'
                              options={IBAN_OPTIONS}
                              wrapperClassName='col-md-6'
                            />
                          </div>
                        </div>
                        <div className='row mb-6'>
                          {/* Sol taraf: Cari Ara butonu ve cari seçimi */}
                          <div className='col-md-6 d-flex align-items-center gap-3'>
                            <button className='bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg'>
                              Cari Ara
                            </button>
                            <AsyncSearchInput<AsyncCustomerModel>
                              name="customerId1"
                              label='Cari Ara'
                              placeholder='Ad Soyad girin...'
                              fetchHandler={fetchAsyncCustomerData}
                              displayField={(item) => `${item.firstName} ${item.lastName}`}
                              onSelect={(item) => console.log('Seçilen cari:', item)}
                              onClear={() => console.log('Cari temizlendi')}
                            />
                          </div>

                          {/* Sağ taraf: Yansıtma Tutarı */}
                          <div className='col-md-6'>
                            <TextInput
                              name='reflectionAmount'
                              label='Yansıtma Tutarı'
                              type='number'
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {tab === 'vehicle' && (
                      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 p-5'>
                        <div className='row mb-6'>
                          <AsyncSearchInput<AsyncCarModel>
                            name="carId"
                            label='Araç Ara'
                            placeholder='Araç bilgisi girin...'
                            fetchHandler={fetchAsyncCarData}
                            displayField={(item) => `${item.plate} (${item.modelYear})`}
                            onSelect={(item) => console.log('Seçilen araç:', item)}
                            onClear={() => console.log('Araç temizlendi')}
                          />
                        </div>

                        <div className='row mb-6'>
                          <TextInput
                            name='plate'
                            label='Plaka'
                            wrapperClassName='col-md-6'
                            readOnly
                          />
                          <TextInput
                            name='brand'
                            label='Marka'
                            wrapperClassName='col-md-6'
                            readOnly
                          />
                        </div>

                        <div className='row mb-6'>
                          <TextInput name='type' label='Tipi' wrapperClassName='col-md-6' />
                          <TextInput
                            name='year'
                            label='Yılı'
                            wrapperClassName='col-md-6'
                            type='date'
                          />
                        </div>

                        <div className='row mb-6'>
                          <TextInput name='gear' label='Vites' wrapperClassName='col-md-6' />
                          <TextInput
                            name='fuelType'
                            label='Yakıt Türü'
                            wrapperClassName='col-md-6'
                          />
                        </div>

                        <div className='row mb-6'>
                          <TextInput
                            name='vehicleGroup'
                            label='Araç Grubu'
                            wrapperClassName='col-md-6'
                          />
                        </div>
                      </div>
                    )}

                    {tab === 'pricing' && (
                      <div className='p-5'>
                        <h5 className='mb-4'>
                          <i className='bi bi-cash-stack me-2'></i>Toplam Fiyatlar
                        </h5>

                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                          <TextInput
                            name='bodyWork'
                            label='KAPORTA İŞÇİLİĞİ'
                            wrapperClassName='col-span-1'
                            type='number'
                          />
                          <TextInput
                            name='paintWork'
                            label='BOYA İŞÇİLİĞİ'
                            wrapperClassName='col-span-1'
                            type='number'
                          />
                          <TextInput
                            name='trimWork'
                            label='TRIM İŞÇİLİĞİ'
                            wrapperClassName='col-span-1'
                            type='number'
                          />
                          <TextInput
                            name='electricWork'
                            label='ELEKTRİK İŞÇİLİĞİ'
                            wrapperClassName='col-span-1'
                            type='number'
                          />
                          <TextInput
                            name='mechanicWork'
                            label='MEKANİK İŞÇİLİĞİ'
                            wrapperClassName='col-span-1'
                            type='number'
                          />
                          <TextInput
                            name='chassisWork'
                            label='ŞASE İŞÇİLİĞİ'
                            wrapperClassName='col-span-1'
                            type='number'
                          />

                          {/* Toplam işçilik alanı */}
                          <TextInput
                            name='totalWork'
                            label='TOPLAM İŞÇİLİK'
                            wrapperClassName='col-span-2'
                            type='number'
                            readOnly
                            value={
                              Number(formik.values.bodyWork || 0) +
                              Number(formik.values.paintWork || 0) +
                              Number(formik.values.trimWork || 0) +
                              Number(formik.values.electricWork || 0) +
                              Number(formik.values.mechanicWork || 0) +
                              Number(formik.values.chassisWork || 0)
                            }
                          />
                        </div>
                      </div>
                    )}

                    {tab === 'fatur' && (
                      <div className='p-5'>
                        <div className='flex justify-end mb-4'>
                          <button type='button' className='btn btn-sm btn-outline-secondary'>
                            <i className='bi bi-list-ul me-2'></i> Kayıtları Göster
                          </button>
                        </div>

                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                          <div className='row mb-6'>
                            <TextInput
                              name='serviceName'
                              label='Servis Adı'
                              wrapperClassName='col-md-6'
                            />

                            <TextInput
                              name='vat'
                              label='KDV'
                              type='number'
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-6'>
                            <TextInput
                              name='amount'
                              label='Tutar'
                              type='number'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='invoiceDate'
                              label='Fatura Tarih'
                              type='date'
                              wrapperClassName='col-md-4'
                            />
                            <TextInput
                              name='invoiceNumber'
                              label='Fatura No'
                              placeholder='No'
                              wrapperClassName='col-md-2'
                            />
                          </div>
                          <div className='row mb-6'>
                            <TextInput
                              name='total'
                              label='Genel Toplam'
                              type='number'
                              readOnly
                              disabled // readOnly yerine disabled kullanılabilir
                              wrapperClassName='col-md-6'
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </Form>
                )}

                {/* Liste Tabı */}
                {tab === 'list' && (
                  <div className='card'>
                    <div className='card-body pt-0'>
                      <MaintenanceTable
                        data={maintenancesList}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        onEdit={(branch) => handleEdit(branch, formik)}
                        onDelete={handleDeleteClick}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        loading={loading}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )
        }}
      </Formik>

      {/* Silme Onay Modalı */}
      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu bakım kaydını silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      {/* Toast Bildirimleri */}
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export {MaintenancePage}
